import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import { notifications as notifApi } from '../services/api';
import toast from 'react-hot-toast';
import { PageLoader, EmptyState } from '../components/Loading';

const TYPE_ICONS = {
  new_request:           'bell',
  quote_received:        'message',
  quote_accepted:        'check',
  order_update:          'package',
  subscription_expiring: 'clock',
  refund:                'refresh',
  tier_upgrade:          'star',
  default:               'circle',
};
function NotifIcon({ type, color }) {
  const icons = {
    bell:     <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    message:  <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    check:    <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    package:  <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    clock:    <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    refresh:  <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
    star:     <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    circle:   <svg viewBox="0 0 24 24" width="20" height="20" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/></svg>,
  };
  return icons[type] || icons.circle;
}

const TYPE_COLORS = {
  new_request:           '#1a3cc8',  // VGO Blue
  quote_received:        '#5c35cc',  // VGO Indigo
  quote_accepted:        '#10b981',  // Green — semantic success
  order_update:          '#1a3cc8',  // VGO Blue
  subscription_expiring: '#e53935',  // VGO Red — urgency
  refund:                '#10b981',  // Green — semantic success
  tier_upgrade:          '#5c35cc',  // VGO Indigo — premium
  default:               '#9ca3af',
};

export default function NotificationsScreen() {
  const C = useTheme();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await notifApi.list();
      setItems(r.notifications || []);
      setUnread(r.unread_count || 0);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleTap = async (n) => {
    if (!n.is_read) {
      await notifApi.markRead(n.id).catch(()=>{});
    }
    if (n.link) nav(n.link);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notifApi.remove(id);
      setItems(items.filter(i => i.id !== id));
      toast.success('Deleted');
    } catch(e) { toast.error('Failed'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await notifApi.markAllRead();
      setItems(items.map(i => ({...i, is_read: true})));
      setUnread(0);
      toast.success('All marked as read');
    } catch(e) { toast.error('Failed'); }
  };

  if (loading) return (
    <div style={{background:C.bg,minHeight:'100vh',padding:'0 0 100px'}}>
      <Header C={C} nav={nav} unread={0} />
      <PageLoader C={C} />
    </div>
  );

  return (
    <div style={{background:C.bg,minHeight:'100vh',padding:'0 0 100px'}}>
      <Header C={C} nav={nav} unread={unread} onMarkAll={handleMarkAllRead} />

      <div style={{padding:'16px'}}>
        {items.length === 0 ? (
          <EmptyState C={C} icon="🔔" title="No notifications" message="You're all caught up!" />
        ) : items.map(n => {
          const icon = TYPE_ICONS[n.type] || TYPE_ICONS.default;
          const color = TYPE_COLORS[n.type] || TYPE_COLORS.default;
          const ago = timeAgo(n.created_at);
          return (
            <div key={n.id} onClick={()=>handleTap(n)} style={{
              background: n.is_read ? C.card : color + '08',
              border: `1px solid ${n.is_read ? C.border : color+'33'}`,
              borderRadius: 14, padding: '12px 14px', marginBottom: 8,
              cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10,
              position: 'relative',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: color + '18', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}><NotifIcon type={icon} color={color}/></div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                  <div style={{fontSize:13, fontWeight: n.is_read ? 500 : 700, color: C.text}}>{n.title}</div>
                  {!n.is_read && <div style={{width:6,height:6,borderRadius:'50%',background:color}}/>}
                </div>
                {n.body && <div style={{fontSize:11,color:C.muted,marginBottom:3,lineHeight:1.4}}>{n.body}</div>}
                <div style={{fontSize:10,color:C.muted}}>{ago}</div>
              </div>
              <button onClick={(e)=>handleDelete(n.id, e)} style={{
                background:'transparent',border:'none',cursor:'pointer',
                color:C.muted,padding:'4px',flexShrink:0,display:'flex',alignItems:'center',
              }}>
                <svg viewBox="0 0 24 24" width="14" height="14" stroke={C.muted} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Header({ C, nav, unread, onMarkAll }) {
  return (
    <div style={{
      background: '#1a3cc8', padding: '16px 16px 14px',
      position: 'sticky', top: 0, zIndex: 100,
    }}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <button onClick={()=>nav(-1)} style={{background:'rgba(255,255,255,0.15)',border:'none',borderRadius:10,width:36,height:36,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg viewBox="0 0 24 24" width="18" height="18" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{flex:1}}>
          <div style={{fontSize:18,fontWeight:800,color:'#fff'}}>Notifications</div>
          {unread > 0 && <div style={{fontSize:11,color:'rgba(255,255,255,0.65)'}}>{unread} unread</div>}
        </div>
        {unread > 0 && onMarkAll && (
          <button onClick={onMarkAll} style={{
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10,
            padding: '6px 12px', color: '#fff', fontSize: 11, fontWeight: 600,
            cursor: 'pointer',
          }}>Mark all read</button>
        )}
      </div>
    </div>
  );
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', {day:'numeric',month:'short'});
}
